import { z } from 'zod';
import { targetingSchema } from './targeting.js';
// --- Type & status registries (Single Source of Truth) ---
export const FLAG_TYPES = ['boolean', 'multivariate'];
export const FLAG_STATUSES = ['active', 'inactive'];
// --- Flag variant ---
export const flagVariantSchema = z
    .object({
    id: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-zA-Z0-9_-]+$/)
        .meta({ description: 'Variant identifier', example: 'blue-button' }),
    value: z.string().max(500).meta({ description: 'Variant value returned to the SDK' }),
    weight: z
        .number()
        .int()
        .min(0)
        .max(10000)
        .meta({ description: 'Traffic allocation in basis points (0-10000)' }),
    payload: z
        .record(z.string(), z.unknown())
        .meta({ description: 'Optional JSON payload attached to this variant' })
        .optional(),
})
    .meta({ id: 'FlagVariant', description: 'A variant for multivariate feature flags' });
// --- Create flag request ---
export const createFlagRequestSchema = z
    .object({
    id: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase kebab-case: my-flag-name')
        .meta({
        description: 'Flag identifier (lowercase kebab-case, used in SDK calls)',
        example: 'new-checkout-flow',
    }),
    name: z.string().min(1).max(200).meta({ description: 'Human-readable flag name' }),
    type: z.enum(FLAG_TYPES).meta({
        description: 'Flag type: boolean (on/off) or multivariate (returns a variant value)',
    }),
    default_value: z
        .union([z.boolean(), z.string().max(500)])
        .meta({ description: 'Value returned when flag is inactive or visitor is outside rollout' }),
    rollout_percentage: z
        .number()
        .int()
        .min(0)
        .max(100)
        .meta({ description: 'Percentage of visitors who see the flag (0-100)' }),
    variants: z
        .array(flagVariantSchema)
        .meta({
        description: 'Variant definitions for multivariate flags (weights must sum to 10000)',
    })
        .max(20)
        .refine((vs) => vs.length === 0 || vs.reduce((s, v) => s + v.weight, 0) === 10000, {
        error: 'Variant weights must sum to 10000 when variants are provided',
    })
        .optional(),
    targeting: targetingSchema.optional(),
})
    .meta({ id: 'CreateFlagRequest', description: 'Create a new feature flag' });
// --- Update flag request ---
export const updateFlagRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    default_value: z.union([z.boolean(), z.string().max(500)]).optional(),
    rollout_percentage: z.number().int().min(0).max(100).optional(),
    variants: z
        .array(flagVariantSchema)
        .max(20)
        .refine((vs) => vs.length === 0 || vs.reduce((s, v) => s + v.weight, 0) === 10000, {
        error: 'Variant weights must sum to 10000 when variants are provided',
    })
        .optional(),
    targeting: targetingSchema.nullable().optional(),
})
    .meta({ id: 'UpdateFlagRequest', description: 'Update an existing flag (all fields optional)' });
// --- Flag status change ---
export const flagStatusChangeSchema = z
    .object({
    status: z
        .enum(FLAG_STATUSES)
        .meta({ description: 'Target status: active (evaluating) or inactive (returns default)' }),
})
    .meta({ id: 'FlagStatusChange', description: 'Toggle flag status' });
//# sourceMappingURL=flags.js.map